/* eslint-disable camelcase */
import * as yup from "yup"
import React, { useCallback, useMemo, useRef } from "react"
import _ from "lodash"
import moment from "moment"
import { Appearance, TextInput, ViewStyle } from "react-native"
import { Cycle, Farm } from "../../../database"
import { DateTimePickerField } from "../../field/date-time-picker-field/date-time-picker-field"
import { FormProps } from "../form"
import { Formik } from "formik"
import { ModelField } from "../../field/model-field/model-field"
import { NumeralField } from "../../field/numeral-field/numeral-field"
import { Text } from "../../text/text"
import { View } from "native-base"
import { color } from "../../../theme"
import { observer } from "mobx-react-lite"
import { translate } from "../../../i18n"
import { WalkthroughElement } from "../../walkthrough-element/walkthrough-element"

const FORM_DROPDOWN: ViewStyle = {
  paddingHorizontal: 16,
  // marginBottom: 8,
  paddingBottom: 8,
  backgroundColor: color.palette.white
}

const TITLE: ViewStyle = {
  borderTopWidth: 1,
  borderColor: color.palette.background,
  paddingHorizontal: 16,
  paddingTop: 16,
  paddingBottom: 10
}

const BORDER: ViewStyle = {
  backgroundColor: color.palette.background,
  height: 4
}

export interface MicrobiologyMultiMeasurementFormProps extends FormProps {
  /**
   * An optional style override useful for padding & margin.
   */
  initialValues?: {
    farm_id?: number,
    pond_id?: number,
    cycle_id?: number,
    measured_date?: string,
    measured_time?: string,
    total_vibrio?: number,
    yellow?: number,
    green?: number,
    black?: number,
    luminance?: number,
    total_bacteria?: number,
  }
}

/**
 * Describe your component here
 */
export const MicrobiologyMultiMeasurementForm = observer(function MicrobiologyMultiMeasurementForm(props: MicrobiologyMultiMeasurementFormProps) {
  const {
    initialValues: initialValuesProps = {},
    onSubmit: onSubmitProps,
    colorScheme = Appearance.getColorScheme(),
    innerRef,
    parentScrollViewRef: scrollViewRef,
    ...rest
  } = props

  const input = {
    // farm_id: useRef<TextInput>(),
    // cycle_id: useRef<TextInput>(),
    // measured_date: useRef<TextInput>(),
    // measured_time: useRef<TextInput>(),

    total_vibrio: useRef<TextInput>(),
    yellow: useRef<TextInput>(),
    green: useRef<TextInput>(),
    black: useRef<TextInput>(),
    luminance: useRef<TextInput>(),
    total_bacteria: useRef<TextInput>(),
  }

  const nextInput = {
    // farm_id: () => input.cycle_id.current && input.cycle_id.current.focus(),
    // cycle_id: () => input.measured_date.current && input.measured_date.current.focus(),
    // measured_date: () => input.measured_time.current && input.measured_time.current.focus(),
    // measured_time: () => input.total_vibrio.current && input.total_vibrio.current.focus(),
    total_vibrio: () => input.yellow.current && input.yellow.current.focus(),
    yellow: () => input.green.current && input.green.current.focus(),
    green: () => input.black.current && input.black.current.focus(),
    black: () => input.luminance.current && input.luminance.current.focus(),
    luminance: () => input.total_bacteria.current && input.total_bacteria.current.focus(),
    total_bacteria: null,
  }

  const validationSchema = yup.object({
    farm_id: yup.number().nullable().required().label(translate("microbiologyMultiMeasurementForm.farmId")),
    cycle_id: yup.number().nullable().required().label(translate("microbiologyMultiMeasurementForm.cycleId")),
    measured_date: yup.mixed().nullable().required().label(translate("microbiologyMultiMeasurementForm.measuredDate")),
    measured_time: yup.mixed().nullable().required().label(translate("microbiologyMultiMeasurementForm.measuredTime")),

    total_vibrio: yup.number().nullable().label(translate("microbiologyMultiMeasurementForm.totalVibrio")),
    yellow: yup.number().nullable().label(translate("microbiologyMultiMeasurementForm.yellow")),
    green: yup.number().nullable().label(translate("microbiologyMultiMeasurementForm.green")),
    black: yup.number().nullable().label(translate("microbiologyMultiMeasurementForm.black")),
    luminance: yup.number().nullable().label(translate("microbiologyMultiMeasurementForm.luminance")),
    total_bacteria: yup.number().nullable().label(translate("microbiologyMultiMeasurementForm.totalBacteria")),
  }).test(function (value) {
    const isValid = !![
      'total_vibrio', 'yellow', 'green', 'black', 'luminance', 'total_bacteria',
    ].find((k) => value[k])
    if (isValid) return true;
    else return this.createError({
      path: 'total_vibrio|yellow|green|black|luminance|total_bacteria',
      message: ''
    })
  })

  const onSubmit = useCallback((values) => {
    onSubmitProps({
      ...values,
      measured_at: values.measured_date
        && values.measured_time
        && moment(`${values.measured_date} ${values.measured_time}`).format('YYYY-MM-DDTHH:mm:ssZ')
        || null,
    })
  }, [onSubmitProps])

  const initialValues = useMemo<MicrobiologyMultiMeasurementFormProps["initialValues"]>(() => ({
    farm_id: null,
    pond_id: null,
    cycle_id: null,
    measured_date: moment().format('YYYY-MM-DD'),
    measured_time: moment().startOf('minutes').format('HH:mm:ss'),
    ..._.omit(initialValuesProps, 'measured_at')
  }), [initialValuesProps])

  return (
    <Formik
      enableReinitialize
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={onSubmit}
      innerRef={innerRef}
      {...rest}
    >
      {({ values, initialValues, setFieldValue, setFieldTouched }) => (
        <>
          <ModelField
            name="farm_id"
            txPrefix="microbiologyMultiMeasurementForm"
            colorScheme={colorScheme}
            formControlProps={{ isRequired: true }}
            modelPickerProps={{
              Model: Farm,
              isDisabled: !!initialValues.farm_id,
              onValueChange: async (value) => {
                await setFieldValue('cycle_id', null)
                await setFieldValue('pond_id', null)
                await setFieldValue('farm_id', value || null)
                await setFieldTouched('farm_id')
              }
            }}
          />
          <View style={TITLE}>
            <Text tx="microbiologyMultiMeasurementForm.microbiology" preset="title3" colorScheme={colorScheme} />
          </View>
          <View style={BORDER} />
          <WalkthroughElement
            id="microbiology-multi-measurement-form"
            scrollViewRef={scrollViewRef}
            scrollOffset={250}
          >
            <ModelField
              key={`${values.farm_id}-cycle_id`}
              name="cycle_id"
              txPrefix="microbiologyMultiMeasurementForm"
              colorScheme={colorScheme}
              formControlProps={{ isRequired: true }}
              modelPickerProps={{
                Model: Cycle,
                dataLabel: "pond_name",
                fetchData: (model, search) => (
                  model.fetch({
                    farm_id__eq: values.farm_id,
                    sort: 'started_at,desc|pond_name_alpha_numeric'
                  }).then((models) => models.filter((m) => (
                    `${m.pond_name} - ${moment(m.started_at).format("DD MMM YYYY")}${m.finished_at ? ` (${translate("Finished")})` : ""}`
                  ).toLowerCase().includes((search || '').toLowerCase())))
                ),
                renderSelectedItemText: (item) => item && `${item.pond_name} - ${moment(item.started_at).format("DD MMM YYYY")}${item.finished_at ? ` (${translate("Finished")})` : ""}`,
                clearable: true,
                isDisabled: !values.farm_id || !!initialValues.cycle_id,
                onValueChange: async (value, index, item) => {
                  await setFieldValue('pond_id', (item || {}).pond_id || null)
                  await setFieldValue('cycle_id', value || null)
                  await setFieldTouched('cycle_id')
                },
                titleTx: "microbiologyMultiMeasurementForm.cycleIdPlaceholder"
              }}
            />
            <DateTimePickerField
              name="measured_date"
              txPrefix="microbiologyMultiMeasurementForm"
              colorScheme={colorScheme}
              formControlProps={{ isRequired: true }}
            />
            <View flexDirection="row">
              <DateTimePickerField
                name="measured_time"
                txPrefix="microbiologyMultiMeasurementForm"
                colorScheme={colorScheme}
                formControlProps={{ flex: 1, isRequired: true }}
                dateTimePickerProps={{ dateTimePickerProps: { mode: "time" } }}
              />
              <View
                flex={1}
                style={FORM_DROPDOWN}
              ></View>
            </View>
            <NumeralField
              name="total_vibrio"
              txPrefix="microbiologyMultiMeasurementForm"
              colorScheme={colorScheme}
              input={input}
              nextInput={nextInput}
            />
            <NumeralField
              name="yellow"
              txPrefix="microbiologyMultiMeasurementForm"
              colorScheme={colorScheme}
              input={input}
              nextInput={nextInput}
            />
            <NumeralField
              name="green"
              txPrefix="microbiologyMultiMeasurementForm"
              colorScheme={colorScheme}
              input={input}
              nextInput={nextInput}
            />
            <NumeralField
              name="black"
              txPrefix="microbiologyMultiMeasurementForm"
              colorScheme={colorScheme}
              input={input}
              nextInput={nextInput}
            />
            <NumeralField
              name="luminance"
              txPrefix="microbiologyMultiMeasurementForm"
              colorScheme={colorScheme}
              input={input}
              nextInput={nextInput}
            />
            <NumeralField
              name="total_bacteria"
              txPrefix="microbiologyMultiMeasurementForm"
              colorScheme={colorScheme}
              input={input}
              nextInput={nextInput}
            />
          </WalkthroughElement>
        </>
      )}
    </Formik>
  )
})

MicrobiologyMultiMeasurementForm.displayName = 'MicrobiologyMultiMeasurementForm'
